import { useState } from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import {
  ApprovalGroupForSelectFragment,
  ApprovalsConfigurationDocument,
  ApprovalsGroupFragment,
  DeleteApprovalGroupDocument,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { ApprovalGroupSelector } from 'modules/approvals/components/ApprovalGroupSelector'

interface NoMembershipsProps {
  deletingGroup: boolean
  onSubmit: (destinationGroup: ApprovalGroupForSelectFragment | null) => void
  onClose: () => void
}

const DeleteGroupNoMembershipsModal = ({
  deletingGroup,
  onSubmit,
  onClose,
}: NoMembershipsProps) => {
  return (
    <Modal
      title={i18n.t('settings.approvals.approval_group.delete.title')}
      description={i18n.t('settings.approvals.approval_group.delete.description')}
      illustration="delete-cross"
      onClose={onClose}
      onSubmit={() => onSubmit(null)}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button type="negative" disabled={deletingGroup} submit>
            {i18n.t('settings.approvals.approval_group.delete.title')}
          </Button>
        </>
      }
    >
      <Box background="champagne400" padding="s16" borderRadius={{ all: 'abs008' }}>
        <Text.Normal>{i18n.t('settings.approvals.approval_group.delete.warning')}</Text.Normal>
      </Box>
    </Modal>
  )
}

interface MoveMembershipsProps {
  group: ApprovalsGroupFragment
  deletingGroup: boolean
  onSubmit: (destinationGroup: ApprovalGroupForSelectFragment | null) => void
  onClose: () => void
}

const DeleteGroupMoveMembershipsModal = ({
  group,
  deletingGroup,
  onSubmit,
  onClose,
}: MoveMembershipsProps) => {
  const [destinationGroup, setDestinationGroup] = useState<ApprovalGroupForSelectFragment | null>(
    null
  )
  return (
    <Modal
      title={i18n.t('settings.approvals.approval_group.delete.title')}
      description={i18n.t('settings.approvals.approval_group.delete.moving.description')}
      illustration="delete-cross"
      onClose={onClose}
      onSubmit={() => onSubmit(destinationGroup)}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button type="negative" disabled={deletingGroup || !destinationGroup} submit>
            {i18n.t('settings.approvals.approval_group.delete.moving.title')}
          </Button>
        </>
      }
    >
      <Box gap="s24">
        <ApprovalGroupSelector
          excludedIds={[group.id]}
          value={destinationGroup}
          onChange={(value) => setDestinationGroup(value)}
          multiple={false}
          label={i18n.t('settings.approvals.approval_group.delete.moving.destination_group')}
          category={group.category}
          width="full"
        />
        <Box background="champagne400" padding="s16" borderRadius={{ all: 'abs008' }}>
          <Text.Normal>
            {i18n.t('settings.approvals.approval_group.delete.moving.warning')}
          </Text.Normal>
        </Box>
      </Box>
    </Modal>
  )
}

interface Props {
  group: ApprovalsGroupFragment
  onClose: () => void
}

const DeleteGroupModal = ({ group, onClose }: Props) => {
  const [deleteApprovalGroup, { loading: deletingGroup }] = useMutation(
    DeleteApprovalGroupDocument,
    {
      extractorFn: (data) => data.approvalsMutations.deleteApprovalsApprovalGroup,
      refetchQueries: [ApprovalsConfigurationDocument],
      awaitRefetchQueries: true,
    }
  )

  useEffect(() => {
    track(trackingEvents.approvals.deleteApprovalGroupStart)
  }, [])

  const onSubmit = async (destinationGroup: ApprovalGroupForSelectFragment | null) => {
    const result = await deleteApprovalGroup({
      variables: {
        id: group.id,
        destinationGroupId: destinationGroup?.id,
      },
    })

    const handled = handleResult(result, (_errors) => {})

    if (handled.success) {
      track(trackingEvents.approvals.deleteApprovalGroupSubmit)
      onClose()
      app.setFlash({
        type: 'correct',
        message: i18n.t('settings.approvals.approval_group.flash_messages.successful_delete'),
        time: 2000,
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('settings.approvals.approval_group.flash_messages.error_delete'),
        time: 2000,
      })
    }

    return handled
  }

  const noMemberships = group.groupMemberships.length === 0

  if (noMemberships) {
    return (
      <DeleteGroupNoMembershipsModal
        deletingGroup={deletingGroup}
        onSubmit={onSubmit}
        onClose={onClose}
      />
    )
  }

  return (
    <DeleteGroupMoveMembershipsModal
      group={group}
      deletingGroup={deletingGroup}
      onSubmit={onSubmit}
      onClose={onClose}
    />
  )
}

export default DeleteGroupModal
